'use client';
import { useLanguage } from '@bells/core/i18n';
import { cn } from '@bells/core/lib/utils';
import { baseLocale } from '@bells/i18n/runtime';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@bells/ui/components/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@bells/ui/components/sidebar';
import { ChevronsUpDown } from 'lucide-react';
import Link from 'next/link';

import { type ElementType, useState } from 'react';

export type Brand = {
  name: string;
  logo: ElementType;
  description: string;
  logoClassName?: string;
};
export const BrandSwitcher = ({ brands }: { brands: Brand[] }) => {
  const { isMobile } = useSidebar();
  const [activeTeam, setActiveTeam] = useState<Brand | undefined>(brands[0]);
  const { language } = useLanguage();
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton className='h-auto items-center !opacity-100 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground dark:text-sidebar-accent-foreground group-data-[collapsible=icon]:lg:!p-1'>
              {activeTeam && (
                <activeTeam.logo
                  className={cn(
                    activeTeam?.logoClassName,
                    'aspect-square lg:!h-6 lg:!w-6'
                  )}
                />
              )}
              <span className='grid flex-1 truncate text-left text-sm font-semibold leading-tight'>
                {activeTeam?.name}
              </span>
              <ChevronsUpDown className='ml-auto' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            align='start'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className='text-xs text-muted-foreground'>
              Brands
            </DropdownMenuLabel>
            {brands.map((brand) => (
              <DropdownMenuItem
                key={brand.name}
                asChild
                className='flex flex-col items-start justify-start gap-2 p-2'
                onClick={() => setActiveTeam(brand)}
              >
                <Link
                  href={language == baseLocale ? `/` : `/${baseLocale}`}
                  prefetch={false}
                >
                  <div className='flex w-full items-center gap-2'>
                    <brand.logo
                      className={cn(
                        '!size-5 shrink-0 overflow-hidden',
                        brand.logoClassName
                      )}
                    />

                    <span className='truncate'>{brand?.name}</span>
                  </div>
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
